/**
 * Facebook Sign-In Client
 *
 * @param {string} appId - Facebook App ID
 * @returns {Object} Client interface with login, logout, and getLoginStatus methods
 *
 * docs: https://developers.facebook.com/docs/facebook-login/web
 * ref: https://developers.facebook.com/docs/javascript
 */
const FacebookSignInClient = (appId) => {
  const clientUrl = "https://connect.facebook.net/hu_HU/sdk.js";
  const clientConfig = {
    appId: appId,
    cookie: true, // Enable cookies to allow the server to access the session
    xfbml: false, // Not using Facebook social plugins (using custom login button)
    version: "v24.0", // Use latest Graph API version
  };

  let client = null;
  let scriptLoadPromise = null;

  const loadClientScript = async () => {
    // Return cached promise to prevent multiple script loads
    if (scriptLoadPromise) {
      return scriptLoadPromise;
    }

    scriptLoadPromise = new Promise((resolve, reject) => {
      if (client) {
        resolve(client);
        return;
      }

      window.fbAsyncInit = () => {
        if (window.FB) {
          client = window.FB;
          client.init(clientConfig);
          resolve(client);
        } else {
          scriptLoadPromise = null; // Reset on failure
          reject({ error: "fbapi_not_available" });
        }
      };

      let script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.onerror = (event) => {
        scriptLoadPromise = null; // Reset on failure
        reject({ error: "fbapi_failed_to_load", event: event });
      };
      document.querySelector("body").appendChild(script);
      script.src = clientUrl;
    });

    return scriptLoadPromise;
  };

  return {
    /**
     * Preload the Facebook SDK without waiting for user interaction
     * This ensures the SDK is ready when the user clicks the login button
     * @returns {Promise} Resolves when client is ready, rejects on error
     */
    preload: async () => {
      return loadClientScript();
    },

    /**
     * Initiate Facebook login flow
     *
     * @param {Function} successHandler - Success callback receiving user data object
     * @param {Function} errorHandler - Error callback receiving error object
     */
    login: async (successHandler, errorHandler) => {
      try {
        const client = await loadClientScript();

        client.login(
          (response) => {
            // Check if user is connected and authorized
            if (response.status === "connected" && response.authResponse) {
              // Get access token
              // https://developers.facebook.com/docs/facebook-login/web/accesstokens
              const accessToken = response.authResponse.accessToken;
              const userId = response.authResponse.userID;

              // Get user profile data including name, email, and picture
              client.api("/me", { fields: "name,email,picture" }, (apiResponse) => {
                if (!apiResponse || apiResponse.error) {
                  errorHandler(apiResponse || { error: "empty_response" });
                } else {
                  successHandler({
                    service: "facebook",
                    id: apiResponse.id || userId,
                    name: apiResponse.name,
                    picture: apiResponse.picture?.data?.url || "",
                    email: apiResponse.email || "",
                    // access token should be verified on the backend
                    // https://developers.facebook.com/docs/facebook-login/guides/advanced/manual-flow
                    id_token: accessToken,
                  });
                }
              });
            } else if (response.status === "not_authorized") {
              // User is logged into Facebook but not authorized the app
              errorHandler({
                error: "not_authorized",
                message: "User is logged into Facebook but has not authorized the app",
              });
            } else {
              // User cancelled login or did not fully authorize
              errorHandler({
                error: "login_cancelled",
                message: "User cancelled login or did not fully authorize",
                response: response,
              });
            }
          },
          {
            scope: "public_profile,email",
            return_scopes: true, // Return the actual granted permissions
          }
        );
      } catch (error) {
        errorHandler(error);
      }
    },

    /**
     * Check the current login status
     *
     * @param {Function} callback - Callback receiving status response
     * @param {Function} errorHandler - Error callback receiving error object
     * @see https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus
     */
    getLoginStatus: async (callback, errorHandler) => {
      try {
        const client = await loadClientScript();
        client.getLoginStatus((response) => {
          callback(response);
        });
      } catch (error) {
        errorHandler(error);
      }
    },

    /**
     * Log out the user
     *
     * @param {Function} callback - Success callback
     * @param {Function} errorHandler - Error callback receiving error object
     * @see https://developers.facebook.com/docs/reference/javascript/FB.logout
     */
    logout: async (callback, errorHandler) => {
      try {
        const client = await loadClientScript();
        client.logout((response) => {
          if (callback) callback(response);
        });
      } catch (error) {
        if (errorHandler) errorHandler(error);
      }
    },
  };
};

export default FacebookSignInClient;
